import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import { useSelector } from "react-redux";
import AdminHeader from "../../common/Header";
import { useNavigate, Link, useParams } from "react-router-dom";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import UploadImage from "./uploadImage";
import "./AddBlog.css";

const EditBlog = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  const navigate = useNavigate();
  const { blogId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    image_url: "",
    author: "",
    content: "",
    excerpt: "",
    meta_title: "",
    meta_description: "",
    tags: "",
    status: "",
    is_home: false,
  });

  const fetchPostData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${baseUrl}/all/blogs/${blogId}`);
      if (response.status === 200) {
        const blogData = response.data.blog;
        setFormData({
          id: blogData.id,
          title: blogData.title,
          image_url: blogData.image_url,
          author: blogData.author,
          content: blogData.content,
          excerpt: blogData.excerpt,
          meta_title: blogData.meta_title,
          meta_description: blogData.meta_description,
          tags: blogData.tags,
          status: blogData.status,
          is_home: blogData.is_home,
        });
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      showToast("Error Occurred", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPostData();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      const payload = {
        ...values,
        id: formData.id,
      };
  
      console.log("Payload for update:", payload);
      const response = await axios.put(`${baseUrl}/admin/editBlog`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      if (response.data.statusType === "SUCCESS") {
        showToast("Blog updated successfully", { type: "success" });
        setTimeout(() => {
          navigate('/blog');
        }, 2000);
      } else {
        showToast(response.data.text, { type: "error" });
      } 
    } catch (error) { 
      console.error("Error updating blog:", error);
      showToast("Error occurred while updating blog", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };
  
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    author: Yup.string().required("Author is required"),
    content: Yup.string().required("Content is required"),
    status: Yup.string().required("Status is required"),
  });
  

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Master", link: "/user" },
    { label: "Blog", link: "/blog" },
    { label: "Edit Blog", link: "", active: true },
  ];

  const textareaFields = [
    { name: "excerpt", label: "Excerpt", rows: 2 },
    { name: "meta_description", label: "Meta Description", rows: 2 },
  ];

  return (
    <>
      <Notification />
      <Container>
        <BreadCrumb items={breadcrumbItems} />
        <Row>
          <Col lg={3} md={4} sm={12}>
            <AdminHeader />
          </Col>
          <Col lg={9} md={8} sm={12}>
            <SingleDetailHeader header="Edit Blog Details" />
            <Formik
              initialValues={formData}
              enableReinitialize
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form className="mb-4 z-index-1">
                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="title" className="form-label">
                          Title
                        </label>
                        <Field
                          type="text"
                          className="form-control"
                          id="title"
                          name="title"
                          placeholder="Enter Title"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="image_url" className="form-label">
                          Image
                        </label>
                        {formData.image_url && (
                          <img 
                            src={formData.image_url}
                            alt="Blog"
                            style={{
                              width: "100%",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        )}
                        <UploadImage
                          baseUrl={baseUrl}
                          onUploadSuccess={(url) => {
                            setFieldValue("image_url", url);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="author" className="form-label">
                          Author
                        </label>
                        <Field
                          type="text"
                          className="form-control"
                          id="author"
                          name="author"
                          placeholder="Enter Author"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="content" className="form-label">
                          Content
                        </label>
                        <CKEditor
                          editor={ClassicEditor}
                          data={values.content}
                          onChange={(event, editor) => {
                            const content = editor.getData();
                            setFieldValue("content", content);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  {textareaFields.map((field, index) => (
                    <Row key={index}>
                      <Col md={12}>
                        <div className="mb-3">
                          <label htmlFor={field.name} className="form-label">
                            {field.label}
                          </label>
                          <Field
                            as="textarea"
                            className="form-control"
                            id={field.name}
                            name={field.name}
                            placeholder={`Enter ${field.label}`}
                            rows={field.rows}
                          />
                        </div>
                      </Col>
                    </Row>
                  ))}

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="meta_title" className="form-label">
                          Meta Title
                        </label>
                        <Field
                          type="text"
                          className="form-control"
                          id="meta_title"
                          name="meta_title"
                          placeholder="Enter Meta Title"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="tags" className="form-label">
                          Tags
                        </label>
                        <Field
                          type="text"
                          className="form-control"
                          id="tags"
                          name="tags"
                          placeholder="Enter Tags"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">
                          Status
                        </label>
                        <Field
                          as="select"
                          className="form-control"
                          id="status"
                          name="status"
                        >
                          <option value="">Select Status</option>
                          <option value="PUBLISHED">Published</option>
                          <option value="DRAFT">Draft</option>
                        </Field>
                      </div>
                    </Col>
                  </Row>

                  <Button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Update"}
                  </Button>
                  <Link
                    style={{ marginLeft: "1rem" }}
                    className="btn btn-primary"
                    to="/blog"
                  >
                    Back
                  </Link>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EditBlog;
